import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { products } from '../mock';

const SearchModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ('');
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return products.slice(0, 6);
    return products
      .filter(p =>
        p.name.toLowerCase().includes(term) ||
        (p.dose || '').toLowerCase().includes(term) ||
        (p.category || '').toLowerCase().includes(term) ||
        (p.tagline || '').toLowerCase().includes(term) ||
        (p.description || '').toLowerCase().includes(term)
      )
      .slice(0, 10);
  }, [q]);

  if (!open) return null;

  const go = (slug) => { onClose(); navigate(`/products/${slug}`); };

  return (
    <div className="fixed inset-0 z-[55] bg-black/85 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
      <div className="max-w-2xl mx-auto mt-24 px-6" onClick={e => e.stopPropagation()}>
        <div className="bg-[#111114] border border-white/10 rounded-lg shadow-[0_40px_120px_-20px_rgba(255,47,161,0.35)] overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
            <Search size={20} className="text-[#ff2fa1] shrink-0" />
            <input
              ref={inputRef}
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search specimens by name, dose, or category…"
              className="flex-1 bg-transparent outline-none text-white text-lg placeholder:text-neutral-500"
            />
            <button onClick={onClose} aria-label="Close" className="text-neutral-500 hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {results.length === 0 && (
              <div className="p-8 text-center text-neutral-500 text-sm">No specimens matched "{q}"</div>
            )}
            {results.map(p => (
              <button key={p.slug} onClick={() => go(p.slug)}
                className="w-full flex items-center gap-4 px-5 py-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors text-left">
                <div className="w-12 h-12 rounded overflow-hidden bg-black/50 shrink-0">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-white text-lg leading-tight">{p.name}</div>
                  <div className="font-mono-label text-[10px] text-neutral-500 mt-0.5">{p.dose} · {p.category}</div>
                </div>
                <div className="text-sm text-neutral-300 shrink-0">{p.priceDisplay}</div>
              </button>
            ))}
          </div>
          <div className="px-5 py-3 border-t border-white/10 text-[10px] font-mono-label text-neutral-500 flex justify-between">
            <span>{q.trim() ? `${results.length} MATCH${results.length === 1 ? '' : 'ES'}` : 'FEATURED SPECIMENS'}</span>
            <span>ESC TO CLOSE</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
